import { Controller } from "@hotwired/stimulus"

/**
 * Tooltip Controller
 *
 * Affiche des tooltips en position fixed pour échapper aux conteneurs overflow-hidden.
 * Remplace les tooltips CSS qui sont clippés par les conteneurs parents.
 *
 * Usage:
 *   <button data-controller="tooltip" data-tooltip-content-value="Mon tooltip">
 *     Hover me
 *   </button>
 */
export default class extends Controller {
  static values = {
    content: String,
    placement: { type: String, default: "top" }
  }

  connect() {
    this.tooltip = null
    this.arrow = null
    this.showTimeout = null
    this.hideTimeout = null

    this.show = this.show.bind(this)
    this.hide = this.hide.bind(this)

    this.element.addEventListener("mouseenter", this.show)
    this.element.addEventListener("mouseleave", this.hide)
    this.element.addEventListener("focus", this.show)
    this.element.addEventListener("blur", this.hide)
  }

  disconnect() {
    this.element.removeEventListener("mouseenter", this.show)
    this.element.removeEventListener("mouseleave", this.hide)
    this.element.removeEventListener("focus", this.show)
    this.element.removeEventListener("blur", this.hide)
    this.removeTooltip()
  }

  show() {
    clearTimeout(this.hideTimeout)

    if (this.tooltip) return

    this.showTimeout = setTimeout(() => {
      this.createTooltip()
      this.positionTooltip()
    }, 100)
  }

  hide() {
    clearTimeout(this.showTimeout)

    this.hideTimeout = setTimeout(() => {
      this.removeTooltip()
    }, 100)
  }

  createTooltip() {
    const isDark = document.documentElement.getAttribute("data-theme") === "dark" ||
                   document.documentElement.classList.contains("dark")

    // Créer le tooltip avec couleurs adaptées au thème
    this.tooltip = document.createElement("div")
    this.tooltip.className = isDark
      ? "fixed z-[1070] px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg whitespace-nowrap pointer-events-none transition-opacity duration-200"
      : "fixed z-[1070] px-3 py-2 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg shadow-lg whitespace-nowrap pointer-events-none transition-opacity duration-200"
    this.tooltip.textContent = this.contentValue
    this.tooltip.style.opacity = "0"

    // Stocker la couleur de la flèche selon le thème
    this.arrowColor = isDark ? "#111827" : "#ffffff"

    // Créer la flèche
    this.arrow = document.createElement("div")
    this.arrow.className = "fixed z-[1070] w-0 h-0 pointer-events-none"
    this.arrow.style.borderWidth = "4px"
    this.arrow.style.borderStyle = "solid"
    this.arrow.style.borderColor = "transparent"

    document.body.appendChild(this.tooltip)
    document.body.appendChild(this.arrow)

    // Animer l'apparition
    requestAnimationFrame(() => {
      if (this.tooltip) {
        this.tooltip.style.opacity = "1"
      }
    })
  }

  positionTooltip() {
    if (!this.tooltip || !this.arrow) return

    const rect = this.element.getBoundingClientRect()
    const tooltipRect = this.tooltip.getBoundingClientRect()
    const padding = 12
    const arrowSize = 4

    let top, left
    let placement = this.placementValue

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Vérifier si le tooltip peut tenir à droite ou en haut
    const canFitTop = rect.top - tooltipRect.height - padding > 0
    const canFitBottom = rect.bottom + tooltipRect.height + padding < viewportHeight
    const canFitLeft = rect.left - tooltipRect.width - padding > 0
    const canFitRight = rect.right + tooltipRect.width + padding < viewportWidth

    // Pour les éléments proches du bord droit, forcer le placement à gauche
    const elementNearRightEdge = rect.right > viewportWidth - 100

    if (elementNearRightEdge && canFitLeft) {
      placement = "left"
    }

    // Calculer la position selon le placement
    switch (placement) {
      case "bottom":
        top = rect.bottom + padding
        left = rect.left + (rect.width - tooltipRect.width) / 2
        break
      case "left":
        top = rect.top + (rect.height - tooltipRect.height) / 2
        left = rect.left - tooltipRect.width - padding
        break
      case "right":
        top = rect.top + (rect.height - tooltipRect.height) / 2
        left = rect.right + padding
        break
      default: // top
        top = rect.top - tooltipRect.height - padding
        left = rect.left + (rect.width - tooltipRect.width) / 2
    }

    // Ajuster left si dépasse à droite
    if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding
    }

    // Ajuster left si dépasse à gauche
    if (left < padding) {
      left = padding
    }

    // Ajuster top si dépasse en haut
    if (top < padding) {
      top = padding
    }

    // Ajuster top si dépasse en bas
    if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding
    }

    // Positionner le tooltip
    this.tooltip.style.top = `${top}px`
    this.tooltip.style.left = `${left}px`

    // Positionner la flèche selon le placement final
    this.styleArrow(placement, rect, tooltipRect, top, left)
  }

  styleArrow(placement, elementRect, tooltipRect, tooltipTop, tooltipLeft) {
    const arrowSize = 4

    // Reset les bordures
    this.arrow.style.borderTopColor = "transparent"
    this.arrow.style.borderBottomColor = "transparent"
    this.arrow.style.borderLeftColor = "transparent"
    this.arrow.style.borderRightColor = "transparent"

    const color = this.arrowColor || "#111827"

    switch (placement) {
      case "bottom":
        this.arrow.style.borderBottomColor = color
        this.arrow.style.top = `${tooltipTop - arrowSize * 2}px`
        this.arrow.style.left = `${elementRect.left + elementRect.width / 2 - arrowSize}px`
        break
      case "left":
        this.arrow.style.borderLeftColor = color
        this.arrow.style.top = `${elementRect.top + elementRect.height / 2 - arrowSize}px`
        this.arrow.style.left = `${tooltipLeft + tooltipRect.width}px`
        break
      case "right":
        this.arrow.style.borderRightColor = color
        this.arrow.style.top = `${elementRect.top + elementRect.height / 2 - arrowSize}px`
        this.arrow.style.left = `${tooltipLeft - arrowSize * 2}px`
        break
      default: // top
        this.arrow.style.borderTopColor = color
        this.arrow.style.top = `${tooltipTop + tooltipRect.height}px`
        this.arrow.style.left = `${elementRect.left + elementRect.width / 2 - arrowSize}px`
    }
  }

  removeTooltip() {
    if (this.tooltip) {
      this.tooltip.remove()
      this.tooltip = null
    }
    if (this.arrow) {
      this.arrow.remove()
      this.arrow = null
    }
  }
}
